import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import authorisationsHelper from "../../../../API/Authorisations/authorisationsHelper";
import secretGenerator from "../../../../API/SecretGenerator";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import AuthorisationSettingsAIView from "../SubComponents/AuthorisationSettingsAIView";

const AuthorisationsSettingsAI = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onAuthCreated,
      selectedId,
      setShowAuthorisationModal,
      onAuthorisationDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();

    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace } = useWorkspaces();
    const [isSaveInProgress, setIsSaveInProgress] = React.useState(false);
    const [updatingSecrets, setUpdatingSecrets] = React.useState(new Set());
    const validationSchema = AppForms.yup.object().shape({
      type: AppForms.yup.string().required().label("Authentication Type"),
      name: AppForms.yup
        .string()
        .required()
        .label("Name")
        .max(255, "Too long, maximum of 255 characters are allowed."),
      description: AppForms.yup.string().label("Description").max(255),
      tokenSecret: AppForms.yup.string().when("type", {
        is: (type) => type === "apiKey",
        then: AppForms.yup.string().required().label("API Key"),
        otherwise: AppForms.yup
          .string()
          .notRequired()
          .nullable()
          .label("Refresh Token"),
      }),
      clientId: AppForms.yup.string().when("type", {
        is: (type) => type === "oauth",
        then: AppForms.yup.string().required().label("Client ID"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      clientSecret: AppForms.yup.string().when("type", {
        is: (type) => type === "oauth",
        then: AppForms.yup.string().required().label("Client Secret"),
        otherwise: AppForms.yup.string().nullable().notRequired(),
      }),
      scope: AppForms.yup.string().notRequired(),
      grantType: AppForms.yup.string().when("type", {
        is: (type) => type === "oauth",
        then: AppForms.yup.string().required().label("Grant Type"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      authorizationBaseUrl: AppForms.yup.string().when("type", {
        is: (type) => type === "oauth",
        then: AppForms.yup.string().required().label("Authorization URL"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      tokenBaseUrl: AppForms.yup.string().when("type", {
        is: (type) => type === "oauth",
        then: AppForms.yup.string().notRequired().label("Token URL"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      //
      desk_username: AppForms.yup.string().when("type", {
        is: (type) => type === "username_and_password",
        then: AppForms.yup.string().required().label("Username"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      desk_password: AppForms.yup.string().when("type", {
        is: (type) => type === "username_and_password",
        then: AppForms.yup.string().required().label("Password"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      basic_username: AppForms.yup.string().when("type", {
        is: (type) => type === "basic",
        then: AppForms.yup.string().required().label("Username"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
      basic_password: AppForms.yup.string().when("type", {
        is: (type) => type === "basic",
        then: AppForms.yup.string().required().label("Password"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
    });

    const handleDeletion = async () => {
      try {
        console.log("result of deletion", opt);
        const result = await authorisationsHelper.deleteAuthorisation(opt.id);
        dispatch("SET_AUTHORISATIONS", [
          ...state.authorisations?.filter((u) => u?.id !== opt.id),
        ]);
        AppToasty.show(t("toasts.authorisationDeleted"), {
          type: "success",
        });
        setShowAuthorisationModal?.(false);
        onAuthorisationDeleted();
      } catch (error) {
        console.log("Error deleting auth", error);
        AppToasty.show(t("toasts.authorisationDeletionFailed"), {
          type: "danger",
        });
      }
    };
    const navigateBack = () => {
      setUpdatingSecrets(new Set());
      setDisplayedView?.(backTo);
    };
    const handleUpdateSecret = (fieldName) => {
      setUpdatingSecrets((prev) => new Set([...prev, fieldName]));
    };
    const identifyOptByID = (id) => {
      return state.authorisations.find((u) => u?.id === id);
    };
    const handleAuthorisationItemSave = async (submitted) => {
      let psw;
      let v = { ...submitted };
      setIsSaveInProgress(true);
      v.workspaceID = getActiveWorkspace()?.id;
      v.type = v.type.toLowerCase();
      if (v?.staticArgs) v.staticArgs = JSON.stringify(v.staticArgs, null, 2);
      delete v?._lastChangedAt;
      delete v?.createdAt;
      delete v?.updatedAt;
      delete v?._deleted;
      delete v?.__typename;
      let oauthTokenSecret;
      if (v?.type === "oauth") {
        psw = v?.clientSecret;
        oauthTokenSecret = v?.tokenSecret;
      } else if (v?.type === "username_and_password")
        psw = JSON.stringify({
          username: v?.desk_username,
          password: v?.desk_password,
        });
      else if (v?.type === "basic")
        psw = JSON.stringify({
          username: v?.basic_username,
          password: v?.basic_password,
        });
      else {
        psw = v?.tokenSecret;
      }
      delete v?.desk_username;
      delete v?.desk_password;
      delete v?.basic_username;
      delete v?.basic_password;
      delete v?.clientSecret;
      delete v?.tokenSecret;
      let newAuthorisations = [...state.authorisations];
      try {
        let result;
        console.log("Is EDIT", isEdit, v);
        if (isEdit) {
          result = await authorisationsHelper.updateAuthorisation(opt.id, v);
          console.log("Edit result", result);
          const _updatedObject = identifyOptByID(
            result.data?.updateAuthorisation?.id
          );
          const updateIndex = newAuthorisations.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newAuthorisations[updateIndex] = result?.data?.updateAuthorisation;

          if (psw && updatingSecrets.has("clientSecret")) {
            const secretResult = await secretGenerator.updateSecret({
              workspaceid: v.workspaceID,
              id: opt.id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status !== "Success") {
              throw new Error("Secret could not be updated");
            }
          }
          if (oauthTokenSecret && updatingSecrets.has("tokenSecret")) {
            // If tokenSecret doesn't exist yet, create it; otherwise update
            const hasExistingTokenSecret = !!opt?.tokenSecret;
            const tokenSecretResult = hasExistingTokenSecret
              ? await secretGenerator.updateSecret({
                  workspaceid: v.workspaceID,
                  id: opt.id,
                  passwordSecretValue: oauthTokenSecret,
                  suffix: "token",
                })
              : await secretGenerator.storeSecret({
                  workspaceid: v.workspaceID,
                  id: opt.id,
                  passwordSecretValue: oauthTokenSecret,
                  suffix: "token",
                });
            if (tokenSecretResult?.data?.status !== "Success") {
              throw new Error("Token secret could not be saved");
            }
            // Update the authorisation record with the SM path
            if (!hasExistingTokenSecret) {
              await authorisationsHelper.updateAuthorisation(opt.id, {
                tokenSecret: tokenSecretResult?.data?.name,
              });
            }
          }
          // For non-oauth types (bearer, apikey, password, env, generic)
          if (
            v?.type !== "oauth" &&
            psw &&
            updatingSecrets.has("tokenSecret")
          ) {
            const secretResult = await secretGenerator.updateSecret({
              workspaceid: v.workspaceID,
              id: opt.id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status !== "Success") {
              throw new Error("Secret could not be updated");
            }
          }
          // For username_and_password type
          if (psw && updatingSecrets.has("desk_credentials")) {
            const secretResult = await secretGenerator.updateSecret({
              workspaceid: v.workspaceID,
              id: opt.id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status !== "Success") {
              throw new Error("Secret could not be updated");
            }
          }
          // For basic auth type
          if (psw && updatingSecrets.has("basic_credentials")) {
            const secretResult = await secretGenerator.updateSecret({
              workspaceid: v.workspaceID,
              id: opt.id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status !== "Success") {
              throw new Error("Secret could not be updated");
            }
          }
        } else {
          result = await authorisationsHelper.createAuthorisation({
            ...v,
          });
          const _id = result?.data?.createAuthorisation?.id;
          const secretResult = await secretGenerator.storeSecret({
            workspaceid: v.workspaceID,
            id: _id,
            passwordSecretValue: psw,
          });
          if (secretResult?.data?.status === "Success") {
            const payload =
              v?.type == "oauth"
                ? {
                    clientSecret: secretResult?.data?.name,
                  }
                : {
                    tokenSecret: secretResult?.data?.name,
                  };
            // For OAuth authorization_code, also store the refresh token
            if (v?.type === "oauth" && oauthTokenSecret) {
              const tokenSecretResult = await secretGenerator.storeSecret({
                workspaceid: v.workspaceID,
                id: _id,
                passwordSecretValue: oauthTokenSecret,
                suffix: "token",
              });
              if (tokenSecretResult?.data?.status === "Success") {
                payload.tokenSecret = tokenSecretResult?.data?.name;
              } else {
                throw new Error("Token secret could not be created");
              }
            }
            const _updatedObject =
              await authorisationsHelper.updateAuthorisation(_id, payload);
          } else {
            throw new Error("Secret could not be created");
          }
          newAuthorisations.push(result?.data?.createAuthorisation);
        }

        dispatch("SET_AUTHORISATIONS", [...newAuthorisations]);
        setUpdatingSecrets(new Set());
        const savedId =
          result?.data?.updateAuthorisation?.id ||
          result?.data?.createAuthorisation?.id;
        backTo && navigateBack();
        onAuthCreated && onAuthCreated("success", savedId, submitted);
      } catch (error) {
        console.error(error);
        setUpdatingSecrets(new Set());
        onAuthCreated && onAuthCreated("error");
      } finally {
        setIsSaveInProgress(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  height: "auto",
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Authorisation : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleAuthorisationItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <AuthorisationSettingsAIView
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isSaveInProgress={isSaveInProgress}
              onUpdateSecret={handleUpdateSecret}
              updatingSecrets={updatingSecrets}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        initialMapValues,
        state.authorisations,
        isEdit,
        AppToasty,
        isSaveInProgress,
        updatingSecrets,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default AuthorisationsSettingsAI;
